import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ChevronRight, 
  ArrowLeft,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Eye,
  Copy,
  Download,
  RefreshCw,
  Settings,
  BarChart3,
  FileText,
  Zap,
  Target,
  Wrench
} from 'lucide-react';
import { 
  getPromptLogEvaluationSummary,
  triggerLogEvaluation,
  getPrompt
} from '../services/api';
import { formatDistanceToNow } from 'date-fns';
import SuggestionModal from '../components/SuggestionModal';

const EvaluationDetail = () => {
  const { promptId, logId } = useParams();
  const navigate = useNavigate();
  const [evaluation, setEvaluation] = useState(null);
  const [prompt, setPrompt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retriggerLoading, setRetriggerLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);

  useEffect(() => {
    loadEvaluationData();
  }, [logId]);

  const loadEvaluationData = async () => {
    try {
      setLoading(true);
      const [evaluationData, promptData] = await Promise.all([
        getPromptLogEvaluationSummary(logId),
        promptId ? getPrompt(promptId) : Promise.resolve(null)
      ]);
      
      setEvaluation(evaluationData);
      setPrompt(promptData);
    } catch (error) {
      console.error('Error loading evaluation data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRetriggerEvaluation = async () => {
    try {
      setRetriggerLoading(true);
      await triggerLogEvaluation(logId);
      // Refresh data after a short delay to allow evaluation to complete
      setTimeout(loadEvaluationData, 2000);
    } catch (error) {
      console.error('Error retriggering evaluation:', error);
    } finally {
      setRetriggerLoading(false);
    }
  };

  const handleFixPrompt = () => {
    setShowSuggestionModal(true);
  };

  const handleSuggestionAccepted = (suggestion) => {
    // Refresh data to show the new version
    loadEvaluationData();
    setShowSuggestionModal(false);
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
    }
  };

  const formatDateTime = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const formatDuration = (ms) => {
    if (!ms) return 'N/A';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-100';
      case 'running':
        return 'text-yellow-600 bg-yellow-100';
      case 'failed':
        return 'text-red-600 bg-red-100';
      case 'pending':
        return 'text-blue-600 bg-blue-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5" />;
      case 'running':
        return <Clock className="h-5 w-5" />;
      case 'fail':
        return <XCircle className="h-5 w-5" />;
      case 'pass':
        return <CheckCircle className="h-5 w-5" />;
      case 'pending':
        return <AlertTriangle className="h-5 w-5" />;
      default:
        return <Eye className="h-5 w-5" />;
    }
  };

  const parseMetadata = (metadata) => {
    if (typeof metadata === 'string') {
      try {
        return JSON.parse(metadata);
      } catch {
        return {};
      }
    }
    return metadata || {};
  };

  const isFailedEvaluation = () => {
    if (!evaluation || !evaluation.log) return false;
    const log = evaluation.log;
    return log.auto_eval_status === 'fail' || log.auto_eval_status === 'failed';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <span className="ml-3 text-gray-600">Loading evaluation details...</span>
      </div>
    );
  }

  if (!evaluation) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Evaluation not found</h3>
        <p className="text-gray-500">The requested evaluation could not be found.</p>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 btn-primary"
        >
          Go Back
        </button>
      </div>
    );
  }

  const log = evaluation.log;
  const summary = evaluation.summary;
  const metadata = parseMetadata(log.metadata);

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center space-x-2 text-sm text-gray-600">
        <Link to="/" className="hover:text-primary-600">Dashboard</Link>
        <ChevronRight className="h-4 w-4" />
        {prompt && (
          <>
            <Link to={`/projects/${prompt.project_id}`} className="hover:text-primary-600">
              {prompt.project_name}
            </Link>
            <ChevronRight className="h-4 w-4" />
            <Link to={`/prompts/${prompt.id}/logs`} className="hover:text-primary-600">
              {prompt.name} Logs
            </Link>
            <ChevronRight className="h-4 w-4" />
          </>
        )}
        <span className="text-gray-900">Evaluation Detail</span>
      </nav>

      {/* Header */}
      <div className="flex justify-between items-start">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="btn-secondary"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Evaluation Detail</h1>
            <p className="text-gray-600 mt-1">
              Detailed analysis of auto-evaluation results
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          {isFailedEvaluation() && prompt && (
            <button
              onClick={handleFixPrompt}
              className="group relative inline-flex items-center justify-center px-3 py-2 font-semibold text-white transition-all duration-300 ease-in-out transform hover:scale-105 active:scale-95 rounded-xl shadow-lg hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-celadon-300/50 bg-gradient-to-r from-celadon-500 via-celadon-400 to-celadon-600 hover:from-celadon-600 hover:via-celadon-500 hover:to-celadon-700 border border-celadon-300/30"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <Wrench className="h-5 w-5 mr-2 relative z-10 group-hover:rotate-12 transition-transform duration-300" />
              <span className="relative z-10 bg-gradient-to-r from-white to-gray-100 bg-clip-text text-transparent font-bold">
                Fix with AI
              </span>
              <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-celadon-400/0 via-white/10 to-celadon-400/0 transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 ease-in-out"></div>
            </button>
          )}
          <button
            onClick={handleRetriggerEvaluation}
            disabled={retriggerLoading}
            className="btn-secondary"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${retriggerLoading ? 'animate-spin' : ''}`} />
            Re-evaluate
          </button>
          <button className="btn-secondary">
            <Download className="h-4 w-4 mr-2" />
            Export
          </button>
        </div>
      </div>

      {/* Status Overview */}
      <div className="bg-white border rounded-lg shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900 flex items-center">
            <BarChart3 className="h-5 w-5 mr-2" />
            Evaluation Overview
          </h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="flex items-center">
              <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(summary.status)}`}>
                {getStatusIcon(summary.status)}
                <span className="ml-2 capitalize">{summary.status}</span>
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Overall Score</div>
              <div className="text-2xl font-bold text-gray-900">
                {summary.avg_score ? `${(summary.avg_score * 100).toFixed(0)}%` : 'N/A'}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Result</div>
              <div className="text-lg font-medium">
                {summary.pass_count > 0 ? (
                  <span className="text-green-600">Pass</span>
                ) : summary.fail_count > 0 ? (
                  <span className="text-red-600">Fail</span>
                ) : (
                  <span className="text-gray-500">Pending</span>
                )}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Completed</div>
              <div className="text-sm text-gray-900">
                {summary.completed_at 
                  ? formatDateTime(summary.completed_at)
                  : 'Not completed'
                }
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Input/Output Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border rounded-lg shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Input</h3>
              <button
                onClick={() => copyToClipboard(log.input_text)}
                className="text-gray-400 hover:text-gray-600"
              >
                <Copy className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div className="p-6">
            <div className="bg-gray-50 rounded-md p-4 max-h-80 overflow-y-auto">
              <pre className="whitespace-pre-wrap text-sm text-gray-900 font-mono">
                {log.input_text}
              </pre>
            </div>
          </div>
        </div>

        <div className="bg-white border rounded-lg shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Output</h3>
              <button
                onClick={() => copyToClipboard(log.output_text)}
                className="text-gray-400 hover:text-gray-600"
              >
                <Copy className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div className="p-6">
            <div className="bg-gray-50 rounded-md p-4 max-h-80 overflow-y-auto">
              <pre className="whitespace-pre-wrap text-sm text-gray-900 font-mono">
                {log.output_text}
              </pre>
            </div>
          </div>
        </div>
      </div>

      {/* Execution Details */}
      <div className="bg-white border rounded-lg shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <Zap className="h-5 w-5 mr-2" />
            Execution Details
          </h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
            <div>
              <div className="text-sm text-gray-500">Model</div>
              <div className="text-sm font-medium text-gray-900">
                {log.model || 'N/A'}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Duration</div>
              <div className="text-sm font-medium text-gray-900">
                {formatDuration(log.execution_time_ms)}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Version</div>
              <div className="text-sm font-medium text-gray-900">
                {log.version || 'N/A'}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Branch</div>
              <div className="text-sm font-medium text-gray-900">
                {log.branch_name || 'N/A'}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-500">API Key</div>
              <div className="text-sm font-medium text-gray-900">
                {log.key_name || 'N/A'}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Logged At</div>
              <div className="text-sm font-medium text-gray-900">
                {formatDateTime(log.logged_at)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Evaluation Results */}
      {(summary.status === 'pass' || summary.status === 'fail') && (
        <div className="bg-white border rounded-lg shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <Target className="h-5 w-5 mr-2" />
              Detailed Evaluation Results
            </h3>
          </div>
          <div className="p-6 space-y-6">
            {/* Criteria Scores (if available) */}
            {log.auto_eval_criteria && (
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-4">Criteria Breakdown</h4>
                <div className="space-y-4">
                  {(() => {
                    try {
                      const criteriaData = typeof log.auto_eval_criteria === 'string' 
                        ? JSON.parse(log.auto_eval_criteria) 
                        : log.auto_eval_criteria;
                      
                      if (criteriaData.scores) {
                        return Object.entries(criteriaData.scores).map(([criterion, score]) => (
                          <div key={criterion} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-900">{criterion}</div>
                              <div className="text-xs text-gray-500">
                                {criteriaData.criteria?.find(c => c.name === criterion)?.description || 'No description'}
                              </div>
                            </div>
                            <div className="flex items-center space-x-3">
                              <div className="w-32 bg-gray-200 rounded-full h-2">
                                <div 
                                  className="bg-blue-600 h-2 rounded-full" 
                                  style={{ width: `${score * 100}%` }}
                                ></div>
                              </div>
                              <div className="text-sm font-medium text-gray-900 w-12">
                                {(score * 100).toFixed(0)}%
                              </div>
                            </div>
                          </div>
                        ));
                      }
                    } catch (e) {
                      return <div className="text-sm text-gray-500">Unable to parse criteria data</div>;
                    }
                  })()}
                </div>
              </div>
            )}

            {/* Full LLM Evaluation Response */}
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-4">AI Evaluator Response</h4>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-700">Complete LLM Response</span>
                  <button
                    onClick={() => copyToClipboard(log.auto_eval_reasoning || 'No reasoning available')}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  <pre className="whitespace-pre-wrap text-sm text-gray-900 font-mono">
                    {log.auto_eval_reasoning || 'No detailed reasoning available'}
                  </pre>
                </div>
              </div>
            </div>

            {/* Raw Evaluation Data */}
            {log.auto_eval_criteria && (
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-4">Raw Evaluation Data</h4>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-gray-700">Complete Evaluation Object</span>
                    <button
                      onClick={() => copyToClipboard(JSON.stringify(log.auto_eval_criteria, null, 2))}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    <pre className="whitespace-pre-wrap text-xs text-gray-700 font-mono">
                      {JSON.stringify(
                        typeof log.auto_eval_criteria === 'string' 
                          ? JSON.parse(log.auto_eval_criteria) 
                          : log.auto_eval_criteria, 
                        null, 
                        2
                      )}
                    </pre>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Metadata */}
      {metadata && Object.keys(metadata).length > 0 && (
        <div className="bg-white border rounded-lg shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <FileText className="h-5 w-5 mr-2" />
              Metadata
            </h3>
          </div>
          <div className="p-6">
            <div className="bg-gray-50 rounded-md p-4">
              <pre className="whitespace-pre-wrap text-sm text-gray-900 font-mono">
                {JSON.stringify(metadata, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      )}

      {/* Copy Success Toast */}
      {copied && (
        <div className="fixed bottom-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg">
          <div className="flex items-center">
            <CheckCircle className="h-4 w-4 mr-2" />
            Copied to clipboard
          </div>
        </div>
      )}

      {/* Suggestion Modal */}
      {showSuggestionModal && prompt && (
        <SuggestionModal
          isOpen={showSuggestionModal}
          onClose={() => setShowSuggestionModal(false)}
          promptId={prompt.id}
          testRunId={null}
          logId={logId}
          originalPrompt={prompt.versions?.find(v => v.is_active)?.content || 'No active prompt version found'}
          onSuggestionAccepted={handleSuggestionAccepted}
        />
      )}
    </div>
  );
};

export default EvaluationDetail; 